import React, { useState } from 'react';
import { Search, Bell, User, Settings, LogOut, Menu, X } from 'lucide-react';
import { useSession } from '../../contexts/SessionContext';
import { useNotifications } from '../../contexts/NotificationContext';
import './AdminNavbar.css';

const AdminNavbar = ({ activeTab, setActiveTab, userEmail, onToggleSidebar, sidebarCollapsed, children }) => {
  const { logout } = useSession();
  const {
    notifications,
    unreadCount,
    liveAlerts,
    dismissLiveAlert,
    markAsRead,
    markAllAsRead,
    browserNotificationPermission,
    enableBrowserNotifications
  } = useNotifications();
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleSearch = (e) => {
    e.preventDefault();
    // Handle search functionality
    console.log('Searching for:', searchQuery);
  };

  const handleNotificationClick = (notification) => {
    if (notification.unread) {
      markAsRead(notification.id);
    }
    if (notification.targetTab) {
      setActiveTab(notification.targetTab);
    }
    setShowNotifications(false);
  };

  const handleMarkAllRead = () => {
    markAllAsRead();
    setShowNotifications(false);
  };

  const canEnableBrowserAlerts = browserNotificationPermission === 'default';

  return (
    <>
      <div className="admin-navbar">
        <div className="admin-navbar-content">
          <div className="admin-navbar-left">
          <button 
            className="admin-menu-toggle"
            onClick={onToggleSidebar}
          >
            {sidebarCollapsed ? <Menu size={20} /> : <X size={20} />}
          </button>
          <div className="admin-brand" aria-label="Brand">
            <div className="admin-brand-logo">
              <img
                className="admin-brand-img"
                src={`${process.env.PUBLIC_URL || ''}/sonickart1.jpg`}
                alt="SoniCKart"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
            <span className="admin-brand-text">SonicKart</span>
          </div>

          <div className="admin-navbar-nav">
            <div className="admin-nav-tabs">
              <button 
                className={`admin-nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => setActiveTab('dashboard')}
              >
                Dashboard
              </button>
              <button 
                className={`admin-nav-tab ${activeTab === 'vendors' ? 'active' : ''}`}
                onClick={() => setActiveTab('vendors')}
              >
                Vendors
              </button>
              <button 
                className={`admin-nav-tab ${activeTab === 'products' ? 'active' : ''}`}
                onClick={() => setActiveTab('products')}
              >
                Products
              </button>
              <button 
                className={`admin-nav-tab ${activeTab === 'orders' ? 'active' : ''}`}
                onClick={() => setActiveTab('orders')}
              >
                Orders
              </button>
              <button 
                className={`admin-nav-tab ${activeTab === 'packages' ? 'active' : ''}`}
                onClick={() => setActiveTab('packages')}
              >
                Packages
              </button>
              <button 
                className={`admin-nav-tab ${activeTab === 'deliveries' ? 'active' : ''}`}
                onClick={() => setActiveTab('deliveries')}
              >
                Deliveries
              </button>
              <button 
                className={`admin-nav-tab ${activeTab === 'delivery-tracking' ? 'active' : ''}`}
                onClick={() => setActiveTab('delivery-tracking')}
              >
                Tracking
              </button>
            </div>
          </div>
        </div>

        <div className="admin-navbar-center">
          <form onSubmit={handleSearch} className="admin-search-form">
            <div className="admin-search-container">
              <Search size={20} className="admin-search-icon" />
              <input
                type="text"
                placeholder="Search vendors, products, orders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="admin-search-input"
              />
            </div>
          </form>
        </div>

        <div className="admin-navbar-right">
          <div className="admin-navbar-actions">
            <div className="admin-notifications">
              <button 
                className="admin-notification-btn"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="admin-notification-badge">{unreadCount}</span>
                )}
              </button>
              
              {showNotifications && (
                <div className="admin-notifications-dropdown">
                  <div className="admin-notifications-header">
                    <h3>Notifications</h3>
                    {notifications.length > 0 && unreadCount > 0 && (
                      <button 
                        className="admin-mark-all-read"
                        onClick={handleMarkAllRead}
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>
                  {browserNotificationPermission !== 'granted' && (
                    <div className="admin-browser-alerts-banner">
                      <div>
                        <strong>Background alerts</strong>
                        <p>
                          {browserNotificationPermission === 'denied'
                            ? 'Browser notifications are blocked. Enable them in browser settings.'
                            : browserNotificationPermission === 'unsupported'
                              ? 'System notifications are not supported in this browser.'
                              : 'Enable alerts to receive updates while the app is in the background.'}
                        </p>
                      </div>
                      {canEnableBrowserAlerts && (
                        <button
                          type="button"
                          className="admin-enable-alerts-btn"
                          onClick={enableBrowserNotifications}
                        >
                          Enable Alerts
                        </button>
                      )}
                    </div>
                  )}
                  <div className="admin-notifications-list">
                    {notifications.length > 0 ? (
                      notifications.map((notification) => (
                        <div 
                          key={notification.id} 
                          className={`admin-notification-item ${notification.unread ? 'unread' : ''}`}
                          onClick={() => handleNotificationClick(notification)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="admin-notification-content">
                            <h4>{notification.title}</h4>
                            <p>{notification.message}</p>
                            <span className="admin-notification-time">{notification.time}</span>
                          </div>
                          {notification.unread && <div className="admin-notification-dot"></div>}
                        </div>
                      ))
                    ) : (
                      <div className="no-notifications">
                        <p>No notifications</p>
                        <span>You're all caught up!</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Session Status Component */}
            <div className="admin-session-slot">
              {children}
            </div>

            <div className="admin-user-menu">
              <button 
                className="admin-user-btn"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="admin-user-avatar">
                  <User size={16} />
                </div>
                <span className="admin-user-name">Admin</span>
              </button>
              
              {showUserMenu && (
                <div className="admin-user-dropdown">
                  <div className="admin-user-info">
                    <div className="admin-user-avatar-large">
                      <User size={20} />
                    </div>
                    <div>
                      <h4>Admin User</h4>
                      <p>{userEmail}</p>
                    </div>
                  </div>
                  <div className="admin-user-menu-items">
                    <button className="admin-user-menu-item">
                      <User size={16} />
                      Profile
                    </button>
                    <button className="admin-user-menu-item">
                      <Settings size={16} />
                      Settings
                    </button>
                    <button 
                      className="admin-user-menu-item logout"
                      onClick={logout}
                    >
                      <LogOut size={16} />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
      {liveAlerts.length > 0 && (
        <div
          style={{
            position: 'fixed',
            right: 20,
            top: 82,
            zIndex: 1400,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            width: 'min(360px, calc(100vw - 32px))'
          }}
        >
          {liveAlerts.map((alert) => (
            <div
              key={alert.id}
              style={{
                background: '#0f172a',
                color: '#fff',
                borderRadius: 12,
                padding: '12px 14px',
                boxShadow: '0 18px 40px rgba(15, 23, 42, 0.28)',
                borderLeft: '4px solid #22c55e'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <Bell size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{alert.title}</div>
                  <div style={{ fontSize: 13, lineHeight: 1.45, color: 'rgba(255,255,255,0.9)' }}>{alert.message}</div>
                  <div style={{ fontSize: 11, marginTop: 6, color: 'rgba(255,255,255,0.65)' }}>{alert.time}</div>
                </div>
                <button
                  type="button"
                  onClick={() => dismissLiveAlert(alert.id)}
                  style={{
                    background: 'transparent',
                    border: 0,
                    color: '#fff',
                    cursor: 'pointer',
                    padding: 0,
                    lineHeight: 0
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="admin-navbar-spacer" aria-hidden="true" />
    </>
  );
};

export default AdminNavbar;
